import {
  ActorAccessLevel,
  ActorVariantTypeId,
  AudioFileFormat,
  AudioPlaybackSetting,
  DriveAccessRoleEnum,
  ProjectAccessRoleEnum,
  ProjectsOrderBy,
  ScriptFormat,
  StudioProjectVersion,
  SubscriptionInterval,
  TakeFeedbackCategory,
  TeamAvatarSettingsAction,
  TeamRole,
  UserAvatarSettingsAction,
  UserIntegrationType,
} from '../../server/graphql/__generated.types';
import Editor from '../context/editor/index.d';
import { CompareSemanticVersionsResult } from '../util/semVerUtils';
import { VoiceTab } from '../components/avatar/contexts/tabs';
import { SignupUpgradeSlides } from '../components/Subscriptions/SubscriptionTiersPrompt/SubscriptionUpgradeModalV2/hooks/useUpgradeModalSlider';
import { StudioCueType } from '../components/StudioCues/Cue';
import { SelectionSetItemId } from '../components/Subscriptions/Onboarding/components/SelectionCard/types';
import { DriveType } from '../components/Drives/types';

export type ProjectType = DriveType;

export type ProjectCreationSource =
  | 'left_nav'
  | 'projects_header'
  | 'workspace_header'
  | 'workspaces_landing_header'
  | 'folder_header'
  | 'new_project_card'
  | 'voices_page'
  | 'studio_page'
  | 'guided_import';

export enum PageType {
  'projects' = 'projects',
  'studio' = 'studio',
  'avatars' = 'voices',
  'phonetic' = 'libraries',
  'resources' = 'resources',
  'api' = 'api',
  'integrations' = 'integrations',
  'account' = 'account',
  'admin' = 'admin',
}

export enum EventName {
  ActorSamplePlayed = 'Actor Sample Played',
  ActorSearchInteraction = 'Actor Search Interaction',
  ActorSelected = 'Actor Selected',
  ActorSelectionPromptOpened = 'Actor Selection Prompt Opened',
  ActorVariantChange = 'Actor Variant Change',
  ActorVariantVersionChanged = 'Actor Variant Version Changed',
  AddTeamMemberCTAClicked = 'Add Team Member CTA Clicked',
  AddTeamMemberSeatsRequestComplete = 'Add Team Member Seats Request Complete',
  AddTeamSeatsRequestCTAClicked = 'Add Team Seats Request CTA Clicked',
  ApiPageExternalLinkClick = 'Api Page External Link Click',
  ApiKeyRequest = 'Api Key Request',
  ApiContactUs = 'API Contact Us',
  AvatarOptions = 'Avatar Options Selected',
  BatchClipsDelete = 'Batch Clip Delete',
  BatchClipsRestore = 'Batch Clip Restore',
  ClearAllCues = 'Clear All Cues',
  ClientVersionMismatchDetected = 'Client Version Mismatch Detected',
  ClipCopiedProject = 'Clip Copied Project',
  ClipDeleted = 'Clip Deleted',
  ClipDownload = 'Clip Download',
  ClipMovedProject = 'Clip Moved Project',
  ClipPreview = 'Clip Preview',
  ClipPreviewPlay = 'Clip Preview Play',
  ClipPreviewDelete = 'Clip Preview Delete',
  ClipPreviewSessionCount = 'Clip Preview Session Count',
  ContinueWithUnsavedPreviews = 'Continue With Unsaved Previews',
  ClipPlay = 'Clip Play',
  ClipRealtime = 'Clip Realtime',
  ClipRenamed = 'Clip Re-named',
  ClipRender = 'Clip Render',
  ClipRenderedBatch = 'Clip Rendered Batch',
  ClipRenderingCanceled = 'Clip Rendering Canceled',
  ClipReorder = 'Clip Re-order',
  ClipReRender = 'Clip Re-render',
  ClipPreviewRepopulateCues = 'Clip Preview Repopulate Cues',
  ClipRestored = 'Clip Restored',
  ClipSearch = 'Clip Search',
  ClipSave = 'Clip Save',
  CommentCreated = 'Comment Created',
  CommentEdited = 'Comment Edited',
  CommentDeleted = 'Comment Deleted',
  CommentViewed = 'Comment Viewed',
  CommentMarkAllAsViewed = 'Comment Mark All As Viewed',
  CuesDisabled = 'Cues Disabled',
  CueSliderUpdated = 'Cue Slider Updated',
  CreateFolder = 'Create Folder',
  EditorUndoPressed = 'Editor Undo Pressed',
  EditorRedoPressed = 'Editor Redo Pressed',
  EditorResized = 'Editor Resized',
  EditorModeChanged = 'Editor Mode Changed',
  Emphasis = 'Emphasis',
  FloatingPhoneticReplacementToggled = 'Floating Phonetic Replacement Toggled',
  FloatingPhoneticReplacementModified = 'Floating Phonetic Replacement Modified',
  FloatingPhoneticReplacementToolbarRender = 'Floating Phonetic Replacement Toolbar Render',
  GlobalAudioFileFormatChanged = 'Global Audio File Format Changed',
  GlobalAudioFileFormatSelectedChanged = 'Global Audio File Format Selected Changed',
  GlobalAudioPlaybackSettingChanged = 'Global Audio Playback Setting Changed',
  HelpClickAdditionalLanguages = 'Help Click - Additional Languages',
  HelpClickBetaProject = 'Help Click - Beta Project',
  HelpClickCues = 'Help Click - Cues',
  HelpClickEditorRespellingToggle = 'Help Click - Editor Respelling Toggle',
  HelpClickLibraryLearnMore = 'Help Click - Library Learn More',
  HelpClickLibraryReplacements = 'Help Click - Library Replacements',
  HelpClickLibraryRespellingGuide = 'Help Click - Library Respelling Guide',
  HelpClickLibraryRespellingToggle = 'Help Click - Library Respelling Toggle',
  IncludeClipScriptFormatsOptionChanged = 'Include Clip Script Formats Option Changed',
  InsightsFilterApplied = 'Insights Filter Applied',
  InsightsLoadMoreClicked = 'Insights "Load More" Clicked',
  InsightsMemberActivityExported = 'Insights Member Activity Exported',
  InvitationAccepted = 'Invitation Accepted',
  InviteSent = 'Invite Sent',
  IntegrationLaunch = 'Integration Launch',
  LeftRailOpen = 'Left Rail Open',
  LeftRailClose = 'Left Rail Close',
  MakerConfirmationPrompt = 'Maker Confirmation Prompt',
  MultipleTTSVersionOptionChanged = 'Multiple TTS Version Option Changed',
  DoNotShowClipDeletionConfirmationModalChanged = 'Do Not Show Clip Deletion Confirmation Modal Changed',
  OpenPlayground = 'Open Playground',
  OnboardingModal = 'Onboarding Modal',
  DoNotShowClipPreviewDeletionConfirmationModalChanged = 'Do Not Show Clip Preview Deletion Confirmation Modal Changed',
  DoNotShowNavigationConfirmationModalChanged = 'Do Not Show Navigation Confirmation Modal Changed',
  DoNotShowDeleteTeamReplacementConfirmationModalChanged = 'Do Not Show Delete Team Replacement Confirmation Modal Changed',
  ImportScript = 'Import Script',
  PageView = 'Page View',
  PauseProjectDefaultUpdated = 'Pause Project Default Updated',
  PauseSectionUpdated = 'Section Pause Updated',
  PauseBulkSectionsUpdated = 'Bulk Section Pause Updated',
  PauseProjectDefaultWarningViewed = 'Pause Project Default Warning Viewed',
  PreviewModelRenderStarted = 'preview_model_render_started',
  PreviewModelSelected = 'preview_model_selected',
  PhoneticDeletion = 'Phonetic Replacement Deleted',
  PhoneticReplacementCreated = 'Phonetic Replacement Created',
  PhoneticReplacementMigrationPrompt = 'Phonetic Replacement Migration Prompt',
  PhoneticReplacementOptionChanged = 'Phonetic Replacement Option Changed',
  PhoneticReplacementPreviewRender = 'Phonetic Replacement Preview Render',
  PhoneticReplacementToggled = 'Phonetic Replacement Toggled',
  PhoneticTabView = 'Phonetic Tab View',
  ProjectCreated = 'Project Created',
  ProjectDownload = 'Project Download',
  ProjectLocked = 'Project Locked',
  ProjectMigrated = 'Project Migrated',
  ProjectReordered = 'Project Reordered',
  ProjectSettingsModalViewed = 'Project Settings Modal Viewed',
  PromptShown = 'Prompt Shown',
  ProjectSearch = 'Project Search',
  ProjectShared = 'Project Shared',
  ProjectSorted = 'Project Sorted',
  ProjectUnlocked = 'Project Unlocked',
  RenderModeOptionChanged = 'Render Mode Option Changed',
  RightRailOpen = 'Right Rail Open',
  RightRailClose = 'Right Rail Close',
  ReplacementsDictionaryShown = 'Replacements Dictionary Shown',
  ScriptStudioSearchCleared = 'Script Studio Search Cleared',
  ScriptStudioSearchFocused = 'Script Studio Search Focused',
  ScriptStudioSearchPerformed = 'Script Studio Search Performed',
  ScriptStudioSectionDragCancelled = 'Script Studio Section Drag Cancelled',
  ScriptStudioSectionDragCompleted = 'Script Studio Section Drag Completed',
  ScriptStudioSectionDragStarted = 'Script Studio Section Drag Started',
  SelfServeTeamAbandonUpgrade = 'Self-Serve Team Abandon Upgrade',
  SelfServeTeamAdjustSeatCount = 'Self-Serve Team Adjust Seat Count',
  SmartSuggestionSelected = 'Smart Suggestion Selected',
  SmartSuggestionsCTAClicked = 'Smart Suggestions CTA Clicked',
  SubscriptionUpgradeAbandoned = 'Subscription Upgrade Abandoned',
  SubscriptionUpgradeAdvance = 'Subscription Upgrade Advance',
  SubscriptionUpgradeConfirmed = 'Subscription Upgrade Confirmed',
  SubscriptionUpgradePromptPlanSelected = 'Subscription Upgrade Prompt Plan Selected',
  SubscriptionUpgradePromptShown = 'Subscription Upgrade Prompt Shown',
  Reformat = 'Reformat',
  SuggestionDenied = 'Suggestion Denied',
  TeamRemoveMember = 'Team Remove Member',
  ToolbarError = 'Toolbar Error',
  TutorialChipSelected = 'Tutorial Chip Selected',
  UnauthenticatedRedirect = 'Unauthenticated Redirect',
  UpsellModalViewed = 'Upsell Modal Viewed',
  UpsellModalCTAClicked = 'Upsell Modal CTA Clicked',
  UserUpdatedAddress = 'User Updated Address',
  UserConductPromptAccepted = 'User Conduct Prompt Accepted',
  UserConductPromptShown = 'User Conduct Prompt Shown',
  UserDismissedAddressPrompt = 'User Dismissed Address Prompt',
  UserPromptedToAddAddress = 'User Prompted To Add Address',
  ViewApiDocumentation = 'View Api Documentation',
  VoiceSearch = 'Voice Search',
  VoiceFilterSelected = 'Voice Filter Selected',
  VoiceTabSelected = 'Voice Tab Selected',
  ContentTileCTAClicked = 'Content Tile CTA Clicked',
  WorkspaceCreated = 'Workspace Created',
  WorkspaceEdited = 'Workspace Edited',
  WorkspaceDeleted = 'Workspace Deleted',
  WorkspaceAccessChanged = 'Workspace Access Changed',
  TakeDownload = 'Take Download',
  TakeCombine = 'Take Combine',
  ScriptStudioProjectDownload = 'Script Studio Project Download',
  TakeRendered = 'Take Rendered',
  TakeRegenerated = 'Take Regenerated',
  TakePlayed = 'Take Played',
  TakeDeleted = 'Take Deleted',
  SectionCreated = 'Section Created',
  SectionRenamed = 'Section Renamed',
  SectionDeleted = 'Section Deleted',
  BulkSectionDeleted = 'Bulk Section Deleted',
  BatchStarted = 'Batch Started',
  BatchCompleted = 'Batch Completed',
  CurrentTakeChanged = 'Current Take Changed',
  ScriptStudioEmotionalPresetSelected = 'Script Studio Emotional Preset Selected',
  ScriptStudioBulkEmotionalPresetSelected = 'Script Studio Bulk Emotional Preset Selected',
  ScriptStudioCueSliderUpdated = 'Script Studio Cue Slider Updated',
  ScriptStudioBulkCueSliderUpdated = 'Script Studio Bulk Cue Slider Updated',
  ScriptStudioCueReset = 'Script Studio Cue Reset',
  ScriptStudioBulkCueReset = 'Script Studio Bulk Cue Reset',
  ScriptStudioNonverbalCueReset = 'Script Studio Nonverbal Cue Reset',
  TakeHistoryViewed = 'Take History Viewed',
  AudioFeedbackSubmitted = 'Audio Feedback Submitted',
  SectionCollapsed = 'Section Collapse Clicked',
  WebVitalsLCP = 'LCP',
  WebVitalsINP = 'INP',
  WebVitalsCLS = 'CLS',
  WebVitalsTTFB = 'TTFB',
  WebVitalsFCP = 'FCP',
  WebVitalsFID = 'FID',
  ProjectTranslation = 'Project Translation',
  ProjectTranslationLanguageSelected = 'Project Translation Language Selected',
  PlaybackModeToggled = 'Playback Mode Toggled',
  AddOnPromptShown = 'Add On Prompt Shown',
  AddOnPurchased = 'Add On Purchased',
  AddOnCanceled = 'Add On Canceled',
  AddOnReactivated = 'Add On Reactivated',
  VoiceCardPreviewPlayed = 'Voice Card Preview Played',
  VoiceAuditionSubmitted = 'Voice Audition Submitted',
  VoiceAuditionBlocked = 'Voice Audition Blocked',
}

type EventType<K extends EventName, V = undefined> = {
  type: K;
} & (V extends undefined ? { data?: V } : { data: V });

export type Event =
  | EventType<
      EventName.ActorSamplePlayed,
      {
        actorId: string;
        actorName: string;
        accessLevel?: ActorAccessLevel | null;
        location: 'list' | 'modal';
        variantId?: string;
      }
    >
  | EventType<
      EventName.ActorSearchInteraction,
      {
        action: 'blur' | 'focus' | 'clear';
        text?: string;
        location: 'list' | 'modal';
      }
    >
  | EventType<
      EventName.ActorSelected,
      {
        actorId: string;
        actorName: string;
        location: 'list' | 'modal';
        tab?: VoiceTab;
      }
    >
  | EventType<
      EventName.ActorSelectionPromptOpened,
      // @todo we should update all avatar selection prompts with path
      { path: string; programmatic?: boolean } | undefined
    >
  | EventType<
      EventName.ActorVariantChange,
      {
        actorId: string;
        variantId: string;
        actorName: string;
        variantTypeId: ActorVariantTypeId;
      }
    >
  | EventType<
      EventName.AvatarOptions,
      {
        avatarId: string;
        variantId?: string;
        action:
          | UserAvatarSettingsAction
          | `Team${TeamAvatarSettingsAction}`
          | 'StartProject'
          | 'SelectAvatar';
        tab?: VoiceTab;
      }
    >
  | EventType<
      EventName.AddTeamMemberCTAClicked,
      {
        source: TeamAccountManagementSource;
      }
    >
  | EventType<EventName.AddTeamMemberSeatsRequestComplete>
  | EventType<
      EventName.AddTeamSeatsRequestCTAClicked,
      {
        source: TeamAccountManagementSource;
      }
    >
  | EventType<
      EventName.FloatingPhoneticReplacementToggled,
      {
        text: string;
        replacement: string;
        enabled: boolean;
      }
    >
  | EventType<
      EventName.FloatingPhoneticReplacementModified,
      {
        text: string;
        previousReplacement: string;
        newReplacement: string;
      }
    >
  | EventType<EventName.FloatingPhoneticReplacementToolbarRender>
  | EventType<
      EventName.ApiPageExternalLinkClick,
      {
        ctaText: string;
        href: string;
        source: 'page' | 'toolbar';
      }
    >
  | EventType<
      EventName.ApiKeyRequest,
      {
        status: ApiKeyRequestStatus;
      }
    >
  | EventType<
      EventName.ApiContactUs,
      {
        ctaText: string;
        href: string;
      }
    >
  | EventType<
      EventName.ViewApiDocumentation,
      {
        ctaText: string;
        href: string;
      }
    >
  | EventType<
      EventName.OpenPlayground,
      {
        ctaText: string;
        href: string;
      }
    >
  | EventType<
      EventName.ClientVersionMismatchDetected,
      {
        actionTaken: 'forceReload' | `none`;
        versionChange?: CompareSemanticVersionsResult;
        clientVersion?: string;
        serverVersion?: string;
      }
    >
  | EventType<EventName.ClipDeleted, { clipId: string; wasRecredited: boolean }>
  | EventType<
      EventName.ClipDownload,
      {
        clipIds: string[];
        audioFormat: string;
        success: boolean;
        elapsedTimeMs: number;
        clipsCount: number;
        combinedTextLength?: number;
        numberOfClipsJustConverted?: number;
        zipFileAlreadyExisted?: boolean;
        actorId?: number;
        actorVariantId?: number;
        actorVariantType?: string;
        actorName?: string;
        ttsVersion?: string;
        error?: string;
        includeScriptFormats?: ScriptFormat[];
        driveType?: DriveType;
      }
    >
  | EventType<
      EventName.ProjectDownload,
      {
        projectId: string;
        audioFormat: string;
        success: boolean;
        elapsedTimeMs: number;
        combinedTextLength?: number;
        clipsCount?: number;
        numberOfClipsJustConverted?: number;
        zipFileAlreadyExisted?: boolean;
        error?: string;
        includeScriptFormats?: ScriptFormat[];
      }
    >
  | EventType<
      EventName.ClipRealtime,
      {
        clipId: string;
        previousProjectId?: string;
        projectId: string;
        actorId: string;
        actorVariantId: string;
        actorVariantType: string;
        actorName: string;
        eventType: ClipEventRealtime;
        textLength: number;
        ttsVersion: string;
      }
    >
  | EventType<EventName.ClipRender, BaseClipRenderEvent>
  | EventType<EventName.ClipPreview, BaseClipRenderEvent>
  | EventType<EventName.ClipPreviewPlay, { actorId: string; clipId: string }>
  | EventType<EventName.ClipPreviewDelete>
  | EventType<
      EventName.ClipPreviewSessionCount,
      { total: number; saved: number }
    >
  | EventType<EventName.ContinueWithUnsavedPreviews>
  | EventType<EventName.ClipPlay, { actorId: string; clipId: string }>
  | EventType<
      EventName.ClipRenderedBatch,
      {
        actorId: string;
        actorVariantId: string;
        actorVariantType: string;
        actorName: string;
        totalClips: number;
        totalLength: number;
        minLength: number;
        maxLength: number;
        meanLength: number;
        renderMode: Editor.StoreState['renderMode'];
      }
    >
  | EventType<
      EventName.ClipReRender,
      {
        clipId: string;
        actorId: string;
        actorVariantId: string;
        actorVariantType: string;
        actorName: string;
        textLength: number;
        isCombined: boolean;
        ttsVersion: string;
      }
    >
  | EventType<
      EventName.ClipPreviewRepopulateCues,
      {
        clipId: string;
        actorId: string;
        actorVariantId: string;
        actorVariantType: string;
        actorName: string;
        textLength: number;
        ttsVersion: string;
      }
    >
  | EventType<EventName.ClipRestored, { clipId: string }>
  | EventType<EventName.ClipSearch, { projectId: string }>
  | EventType<
      EventName.CuesDisabled,
      {
        action: 'tabClick';
      }
    >
  | EventType<
      EventName.BatchClipsDelete,
      { total: number; numRecredited: number }
    >
  | EventType<EventName.BatchClipsRestore, { total: number }>
  | EventType<EventName.EditorResized, { projectId: string; height: number }>
  | EventType<
      EventName.EditorModeChanged,
      { projectId: string; editMode: string }
    >
  | EventType<EventName.InviteSent, { role: string; selfServe: boolean }>
  | EventType<
      EventName.IntegrationLaunch,
      { type: 'canva' | 'adobe'; app?: 'canva' | 'express' | 'premiere' }
    >
  | EventType<EventName.LeftRailOpen, { page: PageType }>
  | EventType<EventName.LeftRailClose, { page: PageType }>
  | EventType<EventName.InvitationAccepted, { role: string }>
  | EventType<EventName.PageView, { page: string }>
  | EventType<
      EventName.ProjectCreated,
      {
        withClientName: boolean;
        studioVersion?: StudioProjectVersion;
        driveType?: DriveType;
        source: ProjectCreationSource;
        tab?: VoiceTab;
        variantId?: string;
      }
    >
  | EventType<EventName.ProjectLocked>
  | EventType<
      EventName.ProjectMigrated,
      {
        projectId: string;
        studioVersion: StudioProjectVersion;
        success: boolean;
      }
    >
  | EventType<EventName.ProjectUnlocked>
  | EventType<
      EventName.PhoneticReplacementCreated,
      {
        path: string;
        type: 'personal' | 'team';
      }
    >
  | EventType<
      EventName.PhoneticReplacementPreviewRender,
      {
        path: string;
        // these fields are optional as they are only available in project detail
        avatarId?: number;
        avatarVariantId?: number;
        version?: string;
        location?: string;
      }
    >
  | EventType<EventName.PromptShown, { prompt: string }>
  | EventType<EventName.PhoneticReplacementOptionChanged, { value: boolean }>
  | EventType<
      EventName.PhoneticReplacementMigrationPrompt,
      {
        projectId: string;
        action: 'start' | 'complete';
        totalReplacements: number;
      }
    >
  | EventType<
      EventName.PhoneticTabView,
      { libraryType: string; location: string }
    >
  | EventType<
      EventName.PhoneticReplacementToggled,
      {
        tab: string;
        type: 'all' | 'single';
        enabled: boolean;
      }
    >
  | EventType<EventName.PhoneticDeletion, { type: 'word' | 'replacement' }>
  | EventType<
      EventName.RenderModeOptionChanged,
      { value: Editor.StoreState['renderMode'] }
    >
  | EventType<EventName.RightRailOpen, { page: PageType }>
  | EventType<EventName.RightRailClose, { page: PageType }>
  | EventType<
      EventName.SubscriptionUpgradePromptShown,
      {
        upgradeContext: string;
        upgradePath: string;
      }
    >
  | EventType<
      EventName.SubscriptionUpgradePromptPlanSelected,
      {
        name: string;
        upgradePath: string;
      }
    >
  | EventType<
      EventName.SubscriptionUpgradeConfirmed,
      {
        addressCountry?: string;
        taxIdType?: string;
        fromPlan?: {
          id: string;
          name: string;
          interval: string;
        } | null;
        toPlan?: {
          id: string;
          name: string;
          interval: string;
        };
        seatCount?: number;
        name: string;
        upgradePath: string;
        success: boolean;
      }
    >
  | EventType<
      EventName.SubscriptionUpgradeAdvance,
      {
        addressCountry?: string;
        taxIdType?: string;
        fromPlan?: {
          id: string;
          name: string;
          interval: string;
        } | null;
        toPlan?: {
          id: string;
          name: string;
          interval: string;
        };
        name: string;
      }
    >
  | EventType<
      EventName.UpsellModalViewed,
      {
        modalType: 'folder_workspace' | 'commenting';
        source?: string;
      }
    >
  | EventType<
      EventName.UpsellModalCTAClicked,
      {
        modalType: 'folder_workspace' | 'commenting';
        cta_label?: string;
      }
    >
  | EventType<
      EventName.UserDismissedAddressPrompt,
      {
        promptSource?: string;
      }
    >
  | EventType<
      EventName.UserPromptedToAddAddress,
      {
        promptSource?: string;
      }
    >
  | EventType<
      EventName.UserUpdatedAddress,
      {
        promptSource?: string;
        prompted: boolean;
        action: 'add' | 'update';
        addressCountry?: string;
        taxIdType?: string;
      }
    >
  | EventType<
      EventName.SubscriptionUpgradeAbandoned,
      {
        fromPlan?: {
          id: string;
          name: string;
          interval: string;
        } | null;
        toPlan?: {
          id: string;
          name: string;
          interval: string;
        };
        slide: SignupUpgradeSlides;
      }
    >
  | EventType<
      EventName.UnauthenticatedRedirect,
      { path: string; detectionLocation: string }
    >
  | EventType<
      EventName.MultipleTTSVersionOptionChanged,
      {
        value: boolean;
      }
    >
  | EventType<
      EventName.DoNotShowClipDeletionConfirmationModalChanged,
      {
        value: boolean;
      }
    >
  | EventType<
      EventName.DoNotShowClipPreviewDeletionConfirmationModalChanged,
      {
        value: boolean;
      }
    >
  | EventType<
      EventName.DoNotShowNavigationConfirmationModalChanged,
      {
        value: boolean;
      }
    >
  | EventType<
      EventName.DoNotShowDeleteTeamReplacementConfirmationModalChanged,
      {
        value: boolean;
      }
    >
  | EventType<
      EventName.GlobalAudioFileFormatSelectedChanged,
      {
        value: boolean;
      }
    >
  | EventType<
      EventName.GlobalAudioFileFormatChanged,
      {
        value: AudioFileFormat;
      }
    >
  | EventType<
      EventName.GlobalAudioPlaybackSettingChanged,
      {
        value: AudioPlaybackSetting;
      }
    >
  | EventType<
      EventName.ImportScript,
      {
        success: boolean;
        contentType: string;
        fileSize?: number;
      }
    >
  | EventType<
      EventName.IncludeClipScriptFormatsOptionChanged,
      {
        value: ScriptFormat[];
      }
    >
  | EventType<
      EventName.InsightsFilterApplied,
      {
        startDate: string;
        endDate: string;
      }
    >
  | EventType<EventName.InsightsLoadMoreClicked, { teamId: string }>
  | EventType<
      EventName.InsightsMemberActivityExported,
      {
        teamId?: string;
        startDate: string;
        endDate: string;
      }
    >
  | EventType<
      EventName.ActorVariantVersionChanged,
      {
        actorId: string;
        variantId: string;
        actorName: string;
        variantTypeId: ActorVariantTypeId;
        versionLabel: string;
        version: string;
      }
    >
  | EventType<
      EventName.MakerConfirmationPrompt,
      {
        interval: SubscriptionInterval;
        action: 'cancel' | 'confirm';
      }
    >
  | EventType<EventName.UserConductPromptShown>
  | EventType<EventName.UserConductPromptAccepted>
  | EventType<
      EventName.ProjectShared,
      | {
          type: ProjectSharedEventType.Anyone;
          projectId: string;
          access?: ProjectAccessRoleEnum;
        }
      | {
          type: ProjectSharedEventType.TeamMember;
          projectId: string;
          teamId: string;
          teamMemberIds: string[];
          access?: ProjectAccessRoleEnum;
        }
    >
  | EventType<EventName.ClipRenderingCanceled>
  | EventType<
      EventName.ProjectSorted,
      {
        type: ProjectsOrderBy;
        projectType: ProjectType;
      }
    >
  | EventType<
      EventName.ProjectSearch,
      {
        search: string;
        projectType: ProjectType;
      }
    >
  | EventType<
      EventName.ProjectReordered,
      {
        projectType: ProjectType;
      }
    >
  | EventType<
      EventName.ClipCopiedProject,
      {
        clipsCount: number;
      }
    >
  | EventType<EventName.HelpClickAdditionalLanguages, { action: 'linkClick' }>
  | EventType<EventName.HelpClickBetaProject, { action: 'linkClick' }>
  | EventType<EventName.HelpClickCues, { action: 'linkClick' }>
  | EventType<
      EventName.HelpClickEditorRespellingToggle,
      { action: 'linkClick' }
    >
  | EventType<EventName.HelpClickLibraryLearnMore, { action: 'linkClick' }>
  | EventType<EventName.HelpClickLibraryReplacements, { action: 'linkClick' }>
  | EventType<
      EventName.HelpClickLibraryRespellingGuide,
      { action: 'linkClick' }
    >
  | EventType<
      EventName.HelpClickLibraryRespellingToggle,
      { action: 'linkClick' }
    >
  | EventType<
      EventName.ClipSave,
      {
        clipId: string;
        projectId: string;
        speakerId: string;
        speakerVariantId: string;
        ttsVersion: string;
      }
    >
  | EventType<
      EventName.ReplacementsDictionaryShown,
      {
        location: ReplacementsDictionaryLocation;
        input: string;
      }
    >
  | EventType<
      EventName.SelfServeTeamAdjustSeatCount,
      {
        teamId: string;
        from: number;
        to: number;
      }
    >
  | EventType<
      EventName.SelfServeTeamAbandonUpgrade,
      {
        currentPlan: string;
        location: string;
      }
    >
  | EventType<
      EventName.TeamRemoveMember,
      {
        teamId: string;
        selfServe: boolean;
        option?: 'remove_with_transfer' | 'remove_only';
      }
    >
  | EventType<
      EventName.TutorialChipSelected,
      {
        tutorialCategory: string;
      }
    >
  | EventType<
      EventName.VoiceSearch,
      {
        searchTerm: string;
        tab: string;
        filters?: string[];
      }
    >
  | EventType<
      EventName.VoiceFilterSelected,
      {
        category: string;
        filter: string;
      }
    >
  | EventType<
      EventName.VoiceTabSelected,
      {
        tab: string;
        teamRole?: TeamRole | null;
      }
    >
  | EventType<
      EventName.ClearAllCues,
      {
        projectId: string;
      }
    >
  | EventType<
      EventName.EditorUndoPressed,
      {
        projectId: string;
      }
    >
  | EventType<
      EventName.EditorRedoPressed,
      {
        projectId: string;
      }
    >
  | EventType<
      EventName.CueSliderUpdated,
      {
        projectId: string;
        actorId: string;
        actorVariantId: string;
        ttsVersion: string;
        cueType: StudioCueType;
        value: number;
      }
    >
  | EventType<
      EventName.ContentTileCTAClicked,
      {
        context: string;
        title: string;
        ctaText: string;
        href: string;
      }
    >
  | EventType<
      EventName.OnboardingModal,
      {
        status: 'completed' | 'error';
        selections?: {
          key: SelectionSetItemId;
          value: string[];
        }[];
      }
    >
  | EventType<
      EventName.CreateFolder,
      {
        driveType?: DriveType;
      }
    >
  | EventType<
      EventName.WorkspaceCreated,
      {
        color: string;
        members: number;
        teamId: string;
      }
    >
  | EventType<
      EventName.WorkspaceEdited,
      {
        members: number;
        teamId: string;
        color?: string;
      }
    >
  | EventType<
      EventName.WorkspaceDeleted,
      {
        workspaceId: string;
      }
    >
  | EventType<
      EventName.WorkspaceAccessChanged,
      {
        workspaceId: string;
        teamId: string;
        teamMemberIds: string[];
        access?: DriveAccessRoleEnum;
      }
    >
  | EventType<
      EventName.CommentCreated,
      {
        type: 'clip' | 'section';
        clipId: string;
        projectId: string;
      }
    >
  | EventType<
      EventName.CommentEdited,
      {
        type: 'clip' | 'section';
        clipId: string;
        projectId: string;
      }
    >
  | EventType<
      EventName.CommentDeleted,
      {
        type: 'clip' | 'section';
        clipId: string;
        projectId: string;
      }
    >
  | EventType<
      EventName.CommentViewed,
      {
        type: 'clip' | 'section';
        clipId: string;
        projectId: string;
        /**
         * True if mark as viewed, false if mark as unviewed
         */
        viewed: boolean;
      }
    >
  | EventType<
      EventName.CommentMarkAllAsViewed,
      {
        type: 'clip' | 'section';
        clipId: string;
        projectId: string;
      }
    >
  | EventType<
      EventName.TakeDownload,
      {
        source: 'section' | 'modal' | 'bulk';
        takeIds: string[];
        audioFormat: string;
        success: boolean;
        elapsedTimeMs: number;
        takesCount: number;
        combinedTextLength?: number;
        numberOfTakesJustConverted?: number;
        zipFileAlreadyExisted?: boolean;
        ttsVersion?: string;
        error?: string;
        includeScriptFormats?: ScriptFormat[];
        driveType?: DriveType;
        integrationType?: UserIntegrationType;
      }
    >
  | EventType<
      EventName.TakeCombine,
      {
        count: number;
        msGapDuration: number;
        filename: string;
      }
    >
  | EventType<
      EventName.ScriptStudioProjectDownload,
      {
        projectId: string;
        audioFormat: string;
        success: boolean;
        elapsedTimeMs: number;
        combinedTextLength?: number;
        numberOfTakes?: number;
        numberOfTakesJustConverted?: number;
        zipFileAlreadyExisted?: boolean;
        error?: string;
        includeScriptFormats?: ScriptFormat[];
      }
    >
  | EventType<EventName.SmartSuggestionsCTAClicked>
  | EventType<
      EventName.SmartSuggestionSelected,
      {
        text: string;
        replacement: string;
      }
    >
  | EventType<EventName.PreviewModelRenderStarted>
  | EventType<
      EventName.PreviewModelSelected,
      {
        modelName: string;
        surface: 'voices_page' | 'section';
        subscriptionTier: string;
      }
    >
  | EventType<
      EventName.TakeRendered,
      {
        takeId: string;
        actorId: number;
        actorVariantId: number;
        actorName: string;
        textLength: number;
        ttsVersion: string;
        customCues: CuesApplied;
        withEmotionalCues: boolean;
        withReplacements: boolean;
        sampleRate: number;
      }
    >
  | EventType<
      EventName.TakeRegenerated,
      {
        takeId: string;
        actorId: number;
        actorVariantId: number;
        actorName: string;
        textLength: number;
        ttsVersion: string;
        customCues: CuesApplied;
        withEmotionalCues: boolean;
        withReplacements: boolean;
        sampleRate: number;
      }
    >
  | EventType<
      EventName.TakePlayed,
      { takeId: string; source: 'section' | 'modal' }
    >
  | EventType<EventName.TakeDeleted, { takeId: string }>
  | EventType<
      EventName.SectionCreated,
      { projectId: string; source: 'projectActions' | 'inline' }
    >
  | EventType<EventName.SectionRenamed, { sectionId: string }>
  | EventType<EventName.SectionDeleted, { sectionId: string }>
  | EventType<EventName.BulkSectionDeleted, { sectionIds: string[] }>
  | EventType<
      EventName.BatchStarted,
      { projectId: string; sectionCount: number }
    >
  | EventType<
      EventName.BatchCompleted,
      {
        projectId: string;
        sectionCount: number;
        successCount: number;
        failureCount: number;
        canceledCount: number;
        elapsedTimeMs: number;
      }
    >
  | EventType<
      EventName.CurrentTakeChanged,
      { sectionId: string; takeId: string }
    >
  | EventType<
      EventName.AudioFeedbackSubmitted,
      {
        sentiment: 'positive' | 'negative';
        hasText: boolean;
        takeId: string;
        speakerId: string;
        categories?: TakeFeedbackCategory[];
      }
    >
  | EventType<
      EventName.ScriptStudioEmotionalPresetSelected,
      {
        sectionId: string;
        emotionalPreset: string;
      }
    >
  | EventType<
      EventName.ScriptStudioBulkEmotionalPresetSelected,
      {
        projectId: string;
        emotionalPreset: string;
      }
    >
  | EventType<
      EventName.ScriptStudioCueSliderUpdated,
      {
        sectionId: string;
        cueType: string;
      }
    >
  | EventType<
      EventName.ScriptStudioBulkCueSliderUpdated,
      {
        projectId: string;
        cueType: string;
      }
    >
  | EventType<EventName.ScriptStudioCueReset, { sectionId: string }>
  | EventType<EventName.ScriptStudioBulkCueReset, { projectId: string }>
  | EventType<EventName.ScriptStudioNonverbalCueReset, { sectionId: string }>
  | EventType<
      EventName.TakeHistoryViewed,
      { sectionId: string; source: 'section' | 'sidebar' }
    >
  | EventType<
      EventName.Emphasis,
      {
        text?: string;
        action: 'applied' | 'requested';
      }
    >
  | EventType<
      EventName.Reformat,
      {
        text?: string;
        replacement?: string;
        action: 'applied' | 'requested' | 'denied';
      }
    >
  | EventType<EventName.ToolbarError, { error?: string }>
  | EventType<
      EventName.SectionCollapsed,
      {
        collapse: boolean;
        selectAll: boolean;
        sectionCount: number;
        totalSectionCount: number;
      }
    >
  | EventType<
      EventName.WebVitalsLCP | EventName.WebVitalsINP | EventName.WebVitalsLCP,
      { value: number; rating: string }
    >
  | EventType<
      EventName.ProjectTranslation,
      {
        sourceProjectId: string;
        translatedProjectId: string;
        sourceLanguage: string;
        targetLanguage: string;
        success: boolean;
        error: string;
      }
    >
  | EventType<
      EventName.ProjectTranslationLanguageSelected,
      {
        projectId: string;
        sourceLanguage: string;
        selectedLanguage: string;
      }
    >
  | EventType<
      EventName.PlaybackModeToggled,
      {
        projectId: string;
        mode: 'continuous' | 'individual';
      }
    >
  | EventType<
      EventName.AddOnPromptShown,
      {
        source:
          | 'usage_meter_card'
          | 'add_on_display_card'
          | 'download_limit_modal';
      }
    >
  | EventType<
      EventName.AddOnPurchased,
      {
        addOnId: string;
        displayName: string;
        amount: number;
        currency: string;
        interval: SubscriptionInterval;
        addOnPlanTier: 'lowest' | 'middle' | 'highest';
      }
    >
  | EventType<
      EventName.AddOnCanceled,
      {
        addOnId: string;
        displayName: string;
        interval: SubscriptionInterval;
        endsAt: number | null;
      }
    >
  | EventType<
      EventName.AddOnReactivated,
      {
        addOnId: string;
        displayName: string;
        interval: SubscriptionInterval;
      }
    >
  | EventType<
      EventName.PauseProjectDefaultUpdated,
      {
        projectId: string;
        value: number;
      }
    >
  | EventType<
      EventName.PauseSectionUpdated,
      {
        projectId: string;
        value?: number;
        resetToDefault: boolean;
      }
    >
  | EventType<
      EventName.PauseBulkSectionsUpdated,
      {
        projectId: string;
        value?: number;
        sectionCount: number;
        resetToDefault: boolean;
      }
    >
  | EventType<
      EventName.ProjectSettingsModalViewed,
      {
        projectId: string;
        source: 'header' | 'projectCard' | 'sectionPause' | 'bulkSectionPause';
      }
    >
  | EventType<
      EventName.PauseProjectDefaultWarningViewed,
      {
        keepCustomPauses: boolean;
      }
    >
  | EventType<
      EventName.ScriptStudioSearchFocused,
      {
        projectId: string;
        sectionCount: number;
      }
    >
  | EventType<
      EventName.ScriptStudioSearchPerformed,
      {
        projectId: string;
        resultCount: number;
        queryLength: number;
      }
    >
  | EventType<
      EventName.ScriptStudioSearchCleared,
      {
        projectId: string;
        location: ScriptStudioSearchClearLocation;
      }
    >
  | EventType<
      EventName.ScriptStudioSectionDragStarted,
      {
        sectionCount: number;
        inputMethod: 'keyboard' | 'mouse';
        projectId: string;
      }
    >
  | EventType<
      EventName.ScriptStudioSectionDragCompleted,
      {
        sectionCount: number;
        inputMethod: 'keyboard' | 'mouse';
        projectId: string;
        originPosition: number;
        destinationPosition: number;
        distance: number;
      }
    >
  | EventType<
      EventName.ScriptStudioSectionDragCancelled,
      {
        sectionCount: number;
        inputMethod: 'keyboard' | 'mouse';
        projectId: string;
        cancelReason: 'escape_key' | 'unknown';
      }
    >
  | EventType<
      EventName.VoiceCardPreviewPlayed,
      {
        name?: string;
        variantId: number;
        source: 'avatar_page' | 'avatar_modal' | 'voice_page_audition';
      }
    >
  | EventType<
      EventName.VoiceAuditionSubmitted,
      {
        inputLength: number;
        resultCount: number;
        success: boolean;
      }
    >
  | EventType<
      EventName.VoiceAuditionBlocked,
      {
        reason?: string;
      }
    >;

export enum ApiKeyRequestStatus {
  start = 'start',
  abandon = 'abandon',
  complete = 'complete',
  error = 'error',
}

/**
 * Where the user cleared the section search from: the search bar's clear
 * button, or the "Clear search" button in the no-results empty state.
 */
export type ScriptStudioSearchClearLocation = 'searchBar' | 'noResults';

export enum ReplacementsDictionaryLocation {
  // Studio page
  studio = 'studio',

  // Pronunciation page
  newWord = 'pronunciation - new word',
  newReplacement = 'pronunciation - new replacement',
  replacementSelected = 'pronunciation - replacement selected',
}

export enum TeamAccountManagementSource {
  accountTeam = 'accountTeam',
  accountPlan = 'accountPlan',
  accountUsage = 'accountUsage',
  projectList = 'projectList',
}

export type CuesApplied = {
  loudness: boolean;
  pace: boolean;
  pause: boolean;
  pitch: boolean;
};

type BaseClipRenderEvent = {
  actorID: string;
  actorVariantID: string;
  actorVariantType: string;
  actorName: string;
  source: string;
  textLength: number;
  ttsVersion: string;
  timeUntilHeadersReceived?: number;
  timeUntilRequestComplete?: number;
  success: boolean;
  renderMode: Editor.StoreState['renderMode'];
  error?: string;
  cues?: CuesApplied;
};

export enum ClipEventRealtime {
  Deleted = EventName.ClipDeleted,
  Render = EventName.ClipRender,
  Reorder = EventName.ClipReorder,
  Renamed = EventName.ClipRenamed,
  MovedProject = EventName.ClipMovedProject,
  Restored = EventName.ClipRestored,
}

export enum ProjectSharedEventType {
  Anyone = 'anyone',
  TeamMember = 'teamMember',
}
